import { useMemo } from 'react';
import { useGetRecordQuery, useGetRecordTypeQuery, useListRecordsQuery } from '@platform/state/api';
import type { IRecord } from '@platform/core/types';
import type { IPickerOption } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { codeQuery } from '../options/codeQuery';
import { lookupColumns } from '../options/lookupColumns';
import { recordNumber } from '../../views/recordNumber';
import { recordOptionLabel } from '../options/recordOptionLabel';
import { valueText } from '../options/valueText';
import type { IRecordChoices } from './IRecordChoices';
import { useDebouncedValue } from './useDebouncedValue';
import { useValueText } from './useValueText';

const PAGE = 25;

/**
 * Записи для поля-посилання (UIUX/field-types/lookup): серверний пошук за кодом і назвою (пауза 200 мс), лише коли список відкрито;
 * пункт — «номер · назва» (без префікса типу, як у прототипі) з уточненням «статус · колонки варіанта lookup»; вибраний запис береться
 * окремо (показ у полі);
 * exclude — що не пропонувати (сам запис і його нащадки в полі «батько»).
 */
export function useRecordChoices(ws: string, target: string, value: string, open: boolean, query: string, exclude?: (r: IRecord) => boolean): IRecordChoices {
  const { data: meta } = useGetRecordTypeQuery(target, { skip: !target });
  const q = useDebouncedValue(codeQuery(query, meta?.recordType.prefix), 200);
  const list = useListRecordsQuery({ ws, rt: target, params: { q: q || undefined, pageSize: PAGE } }, { skip: !open || !target });
  const sel = useGetRecordQuery({ ws, rt: target, id: value }, { skip: !value || !target });
  const strings = useModuleStrings(meta?.module.module.key);
  const fmt = useValueText(strings, target);
  const targetRt = meta?.recordType;
  return useMemo(() => {
    const cols = lookupColumns(targetRt);
    const toOption = (r: IRecord): IPickerOption => {
      const status = targetRt ? strings.status(target, r.status, targetRt.statuses.list.find((s) => s.value === r.status)?.label) : r.status;
      const extra = targetRt ? cols.map((f) => valueText(f, targetRt, r.values[f.key], fmt)) : [];
      return { value: r.id, label: recordOptionLabel(recordNumber(r), r.title), sub: [status, ...extra].filter(Boolean).join(' · ') };
    };
    const items = (list.data?.items ?? []).filter((r) => !exclude?.(r));
    const selected = sel.data && sel.data.id === value ? toOption(sel.data) : null;
    return { options: items.map(toOption), selected, total: list.data?.total ?? 0, loading: list.isFetching, targetRt, moduleKey: meta?.module.module.key };
  }, [list.data, list.isFetching, sel.data, value, exclude, targetRt, strings, target, fmt, meta]);
}
